import * as THREE from 'three';
import { Line2 }        from 'three/examples/jsm/lines/Line2';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface MeasurementOverlay {
  group:   THREE.Group;
  setSize: (w: number, h: number) => void;
  /** Call every frame so labels stay parallel to their lines as the camera rotates. */
  update:  (camera: THREE.Camera, viewW: number, viewH: number) => void;
  dispose: () => void;
}

// ─── Private helpers ──────────────────────────────────────────────────────────

function makeFatLine(
  points:     THREE.Vector3[],
  linewidth:  number,
  lineMats:   LineMaterial[],
  geometries: THREE.BufferGeometry[],
): Line2 {
  const geom = new LineGeometry();
  geom.setPositions(points.flatMap(p => [p.x, p.y, p.z]));
  const mat = new LineMaterial({
    color:      0x000000,
    linewidth,
    transparent: true,
    opacity:    0.80,
    depthTest:  false,
    resolution: new THREE.Vector2(window.innerWidth, window.innerHeight),
  });
  const line = new Line2(geom, mat);
  line.computeLineDistances();
  line.renderOrder = 998;
  lineMats.push(mat);
  geometries.push(geom);
  return line;
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y,     x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x,     y + h, r);
  ctx.arcTo(x,     y + h, x,     y,     r);
  ctx.arcTo(x,     y,     x + w, y,     r);
  ctx.closePath();
}

/** All labels are drawn as horizontal (landscape) canvases — 300×64.
 *  Dynamic rotation via sprite.material.rotation is applied each frame in update(). */
function makeLabel(
  axisWord: string,
  value:    string,
): { sprite: THREE.Sprite; texture: THREE.CanvasTexture; material: THREE.SpriteMaterial } {
  const LW = 300, LH = 64;
  const canvas = document.createElement('canvas');
  canvas.width  = LW;
  canvas.height = LH;
  const ctx = canvas.getContext('2d')!;

  // ── Pill background ──────────────────────────────────────────────────────
  roundRect(ctx, 6, 8, LW - 12, LH - 16, 10);
  ctx.fillStyle = 'rgba(7, 9, 20, 0.88)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.72)';
  ctx.lineWidth   = 1.5;
  ctx.stroke();

  // ── Axis word — bold, all caps ───────────────────────────────────────────
  ctx.font         = 'bold 19px system-ui, -apple-system, sans-serif';
  ctx.fillStyle    = 'rgba(120, 155, 255, 0.95)';
  ctx.textAlign    = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(axisWord, 18, LH / 2);

  // ── Value ────────────────────────────────────────────────────────────────
  ctx.font      = '17px "SF Mono", Consolas, monospace';
  ctx.fillStyle = 'rgba(210, 225, 255, 0.92)';
  ctx.textAlign = 'right';
  ctx.fillText(value, LW - 16, LH / 2);

  const texture  = new THREE.CanvasTexture(canvas);
  const material = new THREE.SpriteMaterial({ map: texture, transparent: true, depthTest: false });
  const sprite   = new THREE.Sprite(material);
  sprite.renderOrder = 1000;
  return { sprite, texture, material };
}

// ─── Public factory ───────────────────────────────────────────────────────────

/**
 * Builds W/H/D annotation lines with labels that auto-rotate each frame
 * to stay parallel to their 3D line as the camera orbits.
 *
 * Call `overlay.update(camera, w, h)` every frame (before the sprite-scene render).
 * Call `overlay.setSize(w, h)` on viewport resize.
 */
export function createMeasurements(model: THREE.Object3D): MeasurementOverlay {
  const group = new THREE.Group();
  group.name  = 'cfg-measurements';

  const textures:   THREE.CanvasTexture[]      = [];
  const spriteMats: THREE.SpriteMaterial[]     = [];
  const dotMats:    THREE.MeshBasicMaterial[]  = [];
  const lineMats:   LineMaterial[]             = [];
  const geometries: THREE.BufferGeometry[]     = [];

  // { sprite, line p0, line p1, perpendicular offset in world space }
  type LabelTrack = { sprite: THREE.Sprite; p0: THREE.Vector3; p1: THREE.Vector3 };
  const labelTracks: LabelTrack[] = [];

  // ── Bounding box — exclude floor/shadow Plane nodes ───────────────────────
  const box = new THREE.Box3();
  model.traverse((child) => {
    if (!(child instanceof THREE.Mesh)) return;
    if (/\bplane\b/i.test(child.name)) return;
    box.expandByObject(child);
  });
  if (box.isEmpty()) box.setFromObject(model);

  const size   = box.getSize(new THREE.Vector3());
  const maxDim = Math.max(size.x, size.y, size.z);
  const off    = maxDim * 0.20;
  const dotR   = maxDim * 0.022;
  const lineW  = 2.0;
  // Label sprite world-space dimensions: width = w, height = w * (LH/LW)
  const labelW = maxDim * 0.38;
  const labelH = labelW * (64 / 300);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const addLine = (pts: THREE.Vector3[]): void => {
    group.add(makeFatLine(pts, lineW, lineMats, geometries));
  };

  const addDot = (pos: THREE.Vector3): void => {
    const geom = new THREE.SphereGeometry(dotR, 8, 6);
    const mat  = new THREE.MeshBasicMaterial({ color: 0x000000, depthTest: false, transparent: true, opacity: 0.85 });
    const mesh = new THREE.Mesh(geom, mat);
    mesh.position.copy(pos);
    mesh.renderOrder = 999;
    geometries.push(geom);
    dotMats.push(mat);
    group.add(mesh);
  };

  /**
   * p0 → p1 defines the line direction used for rotation.
   * The label reads "axisWord … value" left-to-right, which will be aligned
   * from p0 toward p1 in screen space after update() runs.
   */
  const addLabel = (
    axisWord: string, dim: number,
    pos: THREE.Vector3,
    p0:  THREE.Vector3, p1: THREE.Vector3,
  ): void => {
    const { sprite, texture, material } = makeLabel(axisWord, dim.toFixed(2));
    sprite.scale.set(labelW, labelH, 1);
    sprite.position.copy(pos);
    textures.push(texture);
    spriteMats.push(material);
    group.add(sprite);
    labelTracks.push({ sprite, p0: p0.clone(), p1: p1.clone() });
  };

  // ── Width (X) — floor level at front face ────────────────────────────────
  {
    const y  = box.min.y;
    const z  = box.max.z;
    const x0 = box.min.x;
    const x1 = box.max.x;
    const mid = new THREE.Vector3((x0 + x1) / 2, y, z);
    addLine([new THREE.Vector3(x0, y, z), new THREE.Vector3(x1, y, z)]);
    addDot(new THREE.Vector3(x0, y, z));
    addDot(new THREE.Vector3(x1, y, z));
    // Label sits below the line (–Y offset); p0→p1 = left→right so text reads left→right
    addLabel('WIDTH', size.x,
      new THREE.Vector3(mid.x, y - off * 0.38, z),
      new THREE.Vector3(x0, y, z), new THREE.Vector3(x1, y, z));
  }

  const xEdge = box.max.x;

  // ── Height (Y) — right face at front ────────────────────────────────────
  {
    const x  = xEdge;
    const z  = box.max.z;
    const y0 = box.min.y;
    const y1 = box.max.y;
    addLine([new THREE.Vector3(x, y0, z), new THREE.Vector3(x, y1, z)]);
    addDot(new THREE.Vector3(x, y0, z));
    addDot(new THREE.Vector3(x, y1, z));
    // Label sits to the right (+X offset); p0→p1 = bottom→top so text reads bottom→top
    addLabel('HEIGHT', size.y,
      new THREE.Vector3(x + off * 0.38, (y0 + y1) / 2, z),
      new THREE.Vector3(x, y0, z), new THREE.Vector3(x, y1, z));
  }

  // ── Length (Z) — top-right corner, front→back ────────────────────────────
  {
    const x  = xEdge;
    const y  = box.max.y;
    const z0 = box.min.z;  // back
    const z1 = box.max.z;  // front — shared dot with HEIGHT top
    addLine([new THREE.Vector3(x, y, z1), new THREE.Vector3(x, y, z0)]);
    addDot(new THREE.Vector3(x, y, z0));
    // Label above-right (+X, +Y offset); p0→p1 = front→back so text reads front→back
    addLabel('LENGTH', size.z,
      new THREE.Vector3(x + off * 0.38, y + off * 0.30, (z0 + z1) / 2),
      new THREE.Vector3(x, y, z1), new THREE.Vector3(x, y, z0));
  }

  // ── Reusable vectors for update() ─────────────────────────────────────────
  const _v0 = new THREE.Vector3();
  const _v1 = new THREE.Vector3();

  return {
    group,

    setSize: (w: number, h: number) => {
      lineMats.forEach(m => m.resolution.set(w, h));
    },

    /**
     * Projects each label's line endpoints to NDC and sets sprite.material.rotation
     * so the label stays parallel to its line regardless of camera angle.
     */
    update: (camera: THREE.Camera, viewW: number, viewH: number) => {
      labelTracks.forEach(({ sprite, p0, p1 }) => {
        _v0.copy(p0).project(camera);
        _v1.copy(p1).project(camera);
        // Convert NDC → screen-space direction (Y flipped: NDC +Y = screen −Y)
        const dx =  (_v1.x - _v0.x) * viewW;
        const dy = -(_v1.y - _v0.y) * viewH;
        // atan2 in screen space → CCW angle for sprite.material.rotation
        sprite.material.rotation = Math.atan2(-dy, dx);
      });
    },

    dispose: () => {
      textures.forEach(t   => t.dispose());
      spriteMats.forEach(m => m.dispose());
      dotMats.forEach(m    => m.dispose());
      lineMats.forEach(m   => m.dispose());
      geometries.forEach(g => g.dispose());
    },
  };
}
